import { CacheLookupPolicy } from '@azure/msal-browser';
import type { AccountInfo } from '@azure/msal-browser';
import { Button, Text } from '@fluentui/react-components';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';

import { createApiClient } from '../api/client.js';
import type { IHttpClient } from '../api/client.js';
import { getDatalakeAccount, getDatalakePca } from '../auth/datalakeAccount.js';
import { datalakeApiRequest } from '../auth/msalConfig.js';
import { LoadingSpinner } from '../components/LoadingSpinner/LoadingSpinner.js';
import { config } from '../config.js';
import { useAuth } from '../hooks/useAuth/useAuth.js';

interface DataHubClientValue {
  client: IHttpClient;
  userObjectId: string | null;
}

const DataHubClientContext = createContext<DataHubClientValue | null>(null);

export function DataHubClientProvider({ children }: PropsWithChildren) {
  // Reuse the primary account's username as loginHint so the popup pre-fills.
  const { account: primaryAccount } = useAuth();
  const loginHint = primaryAccount?.username;
  // Always start pending so the effect always runs initialize() before any
  // downstream call. Skipping init via a cached-account fast path here causes
  // `uninitialized_public_client_application` on the first acquireTokenSilent.
  const [authStatus, setAuthStatus] = useState<
    'pending' | 'sign-in-required' | 'ready' | 'failed'
  >('pending');
  const [authError, setAuthError] = useState<string | null>(null);
  const [account, setAccount] = useState<AccountInfo | null>(null);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const datalakePca = getDatalakePca();
        await datalakePca.initialize();
        if (cancelled) return;
        // The DataLake cache survives page navigation and reloads, so every
        // page reuses the first DataLake sign-in.
        const cached = getDatalakeAccount(datalakePca);
        setAccount(cached);
        setAuthStatus(cached ? 'ready' : 'sign-in-required');
      } catch (err: unknown) {
        if (cancelled) return;
        setAuthError(
          err instanceof Error ? err.message : 'Datalake init failed',
        );
        setAuthStatus('failed');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Popup must run from a click; browsers block popups opened on page load.
  const signIn = useCallback(async () => {
    setAuthStatus('pending');
    setAuthError(null);
    try {
      const result = await getDatalakePca().acquireTokenPopup({
        ...datalakeApiRequest,
        loginHint,
      });
      const signedIn = result.account ?? getDatalakeAccount(getDatalakePca());
      if (signedIn?.tenantId !== config.datalakeTenantId) {
        throw new Error('Signed in to the wrong tenant for DataLake');
      }
      setAccount(signedIn);
      setAuthStatus('ready');
    } catch (err: unknown) {
      setAuthError(
        err instanceof Error ? err.message : 'Datalake sign-in failed',
      );
      setAuthStatus('failed');
    }
  }, [loginHint]);

  const getToken = useCallback(async (): Promise<string> => {
    if (!account) throw new Error('[DataHubClient] No authenticated account');
    const instance = getDatalakePca();
    try {
      // AccessTokenAndRefreshToken: try the access-token cache, then the
      // refresh-token grant (POST /token). Skips the hidden-iframe fallback
      // entirely so we never wait on a timeout — if neither cached path
      // works, MSAL throws immediately and we surface the popup.
      const { accessToken } = await instance.acquireTokenSilent({
        ...datalakeApiRequest,
        account,
        cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
      });
      return accessToken;
    } catch {
      const { accessToken } = await instance.acquireTokenPopup({
        ...datalakeApiRequest,
        account,
      });
      return accessToken;
    }
  }, [account]);

  const client = useMemo(
    () => createApiClient(getToken, config.datalakeApiBaseUrl),
    [getToken],
  );

  const userObjectId = account?.localAccountId ?? null;

  const value = useMemo(
    () => ({ client, userObjectId }),
    [client, userObjectId],
  );

  if (authStatus === 'sign-in-required' || authStatus === 'failed') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          gap: 16,
        }}
      >
        {authError && (
          <Text role="alert">Datalake sign-in failed: {authError}</Text>
        )}
        <Button appearance="primary" onClick={() => void signIn()}>
          Sign in to DataLake
        </Button>
      </div>
    );
  }

  if (authStatus === 'pending') {
    return (
      <div style={{ height: '100vh' }}>
        <LoadingSpinner label="Connecting to Datalake..." />
      </div>
    );
  }

  return (
    <DataHubClientContext.Provider value={value}>
      {children}
    </DataHubClientContext.Provider>
  );
}

export function useDataHubClient(): IHttpClient {
  const ctx = useContext(DataHubClientContext);
  if (!ctx) {
    throw new Error(
      'useDataHubClient must be used within <DataHubClientProvider>',
    );
  }
  return ctx.client;
}

export function useDataHubUserObjectId(): string | null {
  const ctx = useContext(DataHubClientContext);
  if (!ctx) {
    throw new Error(
      'useDataHubUserObjectId must be used within <DataHubClientProvider>',
    );
  }
  return ctx.userObjectId;
}
